/* oxlint-disable typescript/no-non-null-assertion */
import { useIsomorphicLayoutEffect } from '@cian/react-utils';
import * as React from 'react';
import { useCallback, useRef, useState } from 'react';

export interface IUseSeoBlockCollapseResult {
  containerRef: React.RefObject<HTMLUListElement>;
  collapsedHeightPx: number | null;
  canExpand: boolean;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

function groupChildrenByRow(container: HTMLElement, children: HTMLElement[]): Map<number, HTMLElement[]> {
  const containerRect = container.getBoundingClientRect();
  const rowsMap = new Map<number, HTMLElement[]>();

  children.forEach(el => {
    const rect = el.getBoundingClientRect();
    const top = Math.round(rect.top - containerRect.top + container.scrollTop);
    if (!rowsMap.has(top)) {
      rowsMap.set(top, []);
    }
    rowsMap.get(top)!.push(el);
  });

  return rowsMap;
}

export function useSeoBlockCollapse(itemCount: number, maxRows: number = 3): IUseSeoBlockCollapseResult {
  const containerRef = useRef<HTMLUListElement>(null);
  const [collapsedHeightPx, setCollapsedHeightPx] = useState<number | null>(null);
  const [canExpand, setCanExpand] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const children = Array.from(container.children) as HTMLElement[];
    if (children.length === 0) {
      setCanExpand(false);
      setCollapsedHeightPx(null);

      return;
    }

    const rowsMap = groupChildrenByRow(container, children);
    const rowKeys = Array.from(rowsMap.keys()).sort((a, b) => a - b);
    const needCollapse = rowKeys.length > maxRows;

    setCanExpand(needCollapse);

    if (!needCollapse) {
      setCollapsedHeightPx(null);

      return;
    }

    const keysForCollapsed = rowKeys.slice(0, maxRows);
    const lastRowTop = keysForCollapsed[keysForCollapsed.length - 1];
    const lastRowEls = rowsMap.get(lastRowTop)!;
    const containerRect = container.getBoundingClientRect();
    const lastRowBottom = Math.max(
      ...lastRowEls.map(el => {
        const rect = el.getBoundingClientRect();

        return rect.bottom - containerRect.top + container.scrollTop;
      }),
    );

    setCollapsedHeightPx(lastRowBottom);
  }, [maxRows]);

  useIsomorphicLayoutEffect(() => {
    measure();

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const observer = new ResizeObserver(() => measure());
    observer.observe(container);

    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, itemCount]);

  useIsomorphicLayoutEffect(() => {
    const id = requestAnimationFrame(() => {
      measure();
    });

    return () => cancelAnimationFrame(id);
  }, [measure, itemCount]);

  return {
    containerRef,
    collapsedHeightPx,
    canExpand,
    expanded,
    setExpanded,
  };
}
